import { Button, Space } from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import type { ColumnType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import CustomSelect from "~components/CustomSelect";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { RoleStatuses } from "~enums/roleStatuses";
import type { Roles } from "~enums/roles";
import type { UpdateRole } from "~hooks/apiHooks/useUpdateRole";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { getRoleTranslationKey } from "~utilities/getRoleTranslationKey";
import ImageView from "../../../../../../components/ImageView/ImageView.tsx";
import styles from "../AssignFixtureOfficialsModal.module.css";
import type { AssignFixtureOfficialsModalTableRow } from "../types/assignFixtureOfficialsTableRow";

export const useAssignFixtureOfficialsColumns = (
  isLoading: boolean,
  updateRole: (payload: UpdateRole) => void,
  onDelete: (roleId: string) => void,
  modal: HookAPI,
) => {
  const { formatMessage } = useIntl();

  const { sport = "" } = useParams();

  const { hasPermission } = useUserPermissions();
  const updateRolesAllowed = hasPermission(PermissionNames.ROLES, PermissionOperations.READ);
  const deleteRolesAllowed = hasPermission(PermissionNames.ROLES, PermissionOperations.DELETE);

  const { fixtureOfficialRoles } = useSportConfiguration();

  const [editingKey, setEditingKey] = useState<string | null>(null);

  const columns: ColumnType<AssignFixtureOfficialsModalTableRow>[] = [
    {
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameFullLocal",
      render: (value, row) => (
        <Space size="small">
          <ImageView imageUrl={row.avatarUrl} size={ImageSizes.Small} width={25} height={25} imageType={ImageTypes.PersonHead} />
          {value}
        </Space>
      ),
    },
    {
      title: formatMessage({ id: "role" }),
      dataIndex: "role",
      width: 145,
      sorter: true,
      defaultSortOrder: "ascend" as SortOrder,
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
      render: (role, record) =>
        record.roleId === editingKey ? (
          <CustomSelect
            value={record.role}
            options={fixtureOfficialRoles.map((role) => ({
              label: formatMessage({
                id: `roles.${getRoleTranslationKey(role, sport)}`,
              }),
              value: role,
            }))}
            onChange={(value) => updateRole({ roleId: record.roleId, role: value as Roles })}
            className={styles.rolesSelect}
            showSearch
          />
        ) : (
          formatMessage({
            id: `roles.${getRoleTranslationKey(role, sport)}`,
          })
        ),
      onCell: (record) => ({
        isEditing: record.roleId === editingKey,
        title: formatMessage({ id: "role" }),
      }),
    },
    {
      title: formatMessage({ id: "status" }),
      width: 140,
      dataIndex: "status",
      render: (status, record) =>
        record.roleId === editingKey ? (
          <CustomSelect
            value={record.status}
            options={[RoleStatuses.Active, RoleStatuses.Pending, RoleStatuses.Inactive].map((status) => ({
              label: formatMessage({ id: `roleStatus.${status}` }),
              value: status,
            }))}
            onChange={(value) =>
              updateRole({
                roleId: record.roleId,
                status: value as RoleStatuses,
              })
            }
            className={styles.select}
          />
        ) : status ? (
          formatMessage({ id: `roleStatus.${status}` })
        ) : (
          ""
        ),
      onCell: (record) => ({
        isEditing: record.roleId === editingKey,
        title: formatMessage({ id: "status" }),
      }),
    },
    {
      align: "right",
      width: 80,
      render: (_value, row) => {
        const isEditing = row.roleId === editingKey;
        return (
          <>
            {updateRolesAllowed && (
              <Button
                type="text"
                size="small"
                title={formatMessage({
                  id: "fixtureListPage.table.column.actions.inlineEdit",
                })}
                icon={<i className={isEditing ? "fas fa-pen-clip" : "fas fa-pen"} />}
                onClick={() => setEditingKey(isEditing ? null : row.roleId)}
                disabled={isLoading}
              />
            )}
            {deleteRolesAllowed && (
              <Button
                type="text"
                size="small"
                icon={<i className="fas fa-trash-can mr-2" />}
                onClick={() => {
                  modal.confirm({
                    title: formatMessage({
                      id: "officials.delete.confirm.title",
                    }),
                    content: formatMessage({ id: "delete.item.confirm" }),
                    okText: formatMessage({ id: "delete" }),
                    onOk: () => onDelete(row.roleId),
                  });
                }}
                disabled={isLoading}
              />
            )}
          </>
        );
      },
    },
  ];

  return columns;
};
